import {useEffect,useRef,useState} from 'react';

import {exampleLanguage} from './exampleLanguages';
const messages={
    'not-allowed':'Microphone permission was denied. Allow microphone access in site settings, then turn voice examples off and on.',
    'service-not-allowed':'Your browser has disabled speech recognition. You can still show examples manually.',
    'audio-capture':'No working microphone was found. Check the faculty microphone, then turn voice examples off and on.',
    network:'The speech service could not connect. Check your connection, then turn voice examples off and on.',
    'language-not-supported':'This speech language is unavailable. Choose another language, then turn voice examples off and on.',
    aborted:'Voice recognition was interrupted. Turn voice examples off and on to resume.',
    unstable:'Your browser keeps stopping speech recognition. Turn voice examples off and on, or show examples manually.',
    timeout:'The microphone did not start. Check browser permissions, then turn voice examples off and on.',
    command:'The recognized example could not be shown. You can show it manually, or turn voice examples off and on to retry.',
};

// Faculty controls opt-in; students must never mount an enabled microphone hook.
// Speech is processed by the browser's recognition service, which may be online.
export default function useLectureExampleVoice({enabled=false,onCommand,language='en-IN',disabled=false,onSpeechStart,onSpeechEnd,onInterim}) {
    const supported=typeof window!=='undefined' && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
    const [visible,setVisible]=useState(()=>typeof document==='undefined' || !document.hidden);
    const [listening,setListening]=useState(false),[transcript,setTranscript]=useState('');
    const [error,setError]=useState(''),[status,setStatus]=useState('off');
    const commandRef=useRef(onCommand),blocked=useRef(''),lastEmission=useRef(null);
    commandRef.current=onCommand;
    const speechEvents=useRef({onSpeechStart,onSpeechEnd,onInterim});
    speechEvents.current={onSpeechStart,onSpeechEnd,onInterim};
    const wantsInterim=typeof onInterim==='function';
    useEffect(()=>{
        const changed=()=>setVisible(!document.hidden);
        document.addEventListener('visibilitychange',changed);
        return()=>document.removeEventListener('visibilitychange',changed);
    },[]);
    useEffect(()=>{
        setListening(false);
        if(!enabled) {
            blocked.current='';lastEmission.current=null;setTranscript('');setError('');setStatus('off');return undefined;
        }
        if(!supported) {setError('Voice examples are unavailable in this browser. Use the manual example controls.');setStatus('unsupported');return undefined;}
        if(blocked.current) {setError(blocked.current);setStatus('error');return undefined;}
        if(disabled || !visible) {setStatus('paused');return undefined;}
        if(window.isSecureContext===false) {
            blocked.current='Microphone recognition requires HTTPS. You can still show examples manually.';
            setError(blocked.current);setStatus('error');return undefined;
        }
        setError('');
        let disposed=false,current=null,restartTimer=null,startTimer=null,endTimer=null,emptyEnds=0;
        let starts=[];
        const clearTimers=()=>{clearTimeout(restartTimer);clearTimeout(startTimer);clearTimeout(endTimer);};
        const detach=(abort=false)=>{
            const old=current;current=null;
            if(!old)return;
            old.onstart=old.onresult=old.onend=old.onerror=old.onspeechstart=old.onspeechend=old.onaudioend=null;
            if(abort)try{old.abort();}catch{/* The browser may already have stopped. */}
        };
        const fail=code=>{
            if(disposed)return;
            blocked.current=messages[code] || 'Voice recognition stopped. Turn voice examples off and on, or show an example manually.';
            clearTimers();detach(true);setListening(false);setError(blocked.current);setStatus('error');
        };
        const schedule=()=>{
            if(disposed || blocked.current || document.hidden)return;
            setListening(false);setStatus('reconnecting');
            clearTimeout(restartTimer);
            restartTimer=setTimeout(start,Math.min(5000,400*2**Math.min(emptyEnds,4)));
        };
        function start() {
            if(disposed || blocked.current || document.hidden)return;
            // Stop a broken engine's rapid start/end loop; normal quiet sessions
            // can end and reconnect without repeated permission requests.
            const now=Date.now();starts=starts.filter(time=>now-time<20000);
            if(starts.length>=6){fail('unstable');return;}starts.push(now);
            let recognition;
            try {
                const Recognition=window.SpeechRecognition || window.webkitSpeechRecognition;
                recognition=new Recognition();
                recognition.lang=exampleLanguage(language);
                recognition.continuous=true;recognition.interimResults=wantsInterim;recognition.maxAlternatives=1;
            } catch {fail('service-not-allowed');return;}
            current=recognition;
            const delivered=new Set();let hadResult=false;
            const valid=()=>!disposed && current===recognition && !blocked.current;
            const ended=()=>{
                if(!valid())return;
                clearTimeout(startTimer);clearTimeout(endTimer);speechEvents.current.onSpeechEnd?.();detach();
                emptyEnds=hadResult?0:emptyEnds+1;schedule();
            };
            recognition.onstart=()=>{
                if(!valid())return;
                clearTimeout(startTimer);setListening(true);setStatus('listening');
            };
            recognition.onspeechstart=()=>{if(valid() && !document.hidden)speechEvents.current.onSpeechStart?.();};
            recognition.onspeechend=recognition.onaudioend=()=>{if(valid() && !document.hidden)speechEvents.current.onSpeechEnd?.();};
            recognition.onresult=event=>{
                if(!valid() || document.hidden)return;
                const results=event.results || [];
                for(let index=Math.max(0,event.resultIndex || 0);index<results.length;index++) {
                    const result=results[index];
                    if(!result?.isFinal || delivered.has(index))continue;
                    delivered.add(index);
                    const text=String(result[0]?.transcript || '').trim();
                    if(!text)continue;
                    hadResult=true;emptyEnds=0;starts=[];setTranscript(text.slice(-1200));
                    const normalized=text.toLowerCase().replace(/[.,!?;:]+/g,'').replace(/\s+/g,' ').trim(),now=Date.now();
                    if(lastEmission.current?.text===normalized && now-lastEmission.current.time<2000)continue;
                    lastEmission.current={text:normalized,time:now};
                    try {
                        Promise.resolve(commandRef.current?.(text)).catch(()=>{if(valid())fail('command');});
                    } catch {fail('command');break;}
                }
                if(valid() && wantsInterim) {
                    const interim=Array.from(results).filter(result=>!result.isFinal).map(result=>result[0]?.transcript || '').join(' ').trim();
                    speechEvents.current.onInterim?.(interim);
                    if(interim)setTranscript(interim.slice(-1200));
                }
            };
            recognition.onend=ended;
            recognition.onerror=event=>{
                if(!valid())return;
                if(event.error==='no-speech') {
                    // Some engines omit onend after no-speech. End this cycle once.
                    clearTimeout(endTimer);
                    endTimer=setTimeout(()=>{if(valid()){speechEvents.current.onSpeechEnd?.();detach(true);emptyEnds++;schedule();}},1500);
                    return;
                }
                fail(event.error);
            };
            setStatus('starting');
            startTimer=setTimeout(()=>{if(valid())fail('timeout');},12000);
            try {recognition.start();} catch {fail('not-allowed');}
        }
        start();
        return()=>{disposed=true;clearTimers();detach(true);};
    },[enabled,disabled,supported,visible,language,wantsInterim]);
    return {supported,listening,transcript,error,status};
}
